import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";

const ENQUIRY_TYPES = [
  "Residential Air Conditioning",
  "Commercial Air Conditioning",
  "Builder or Developer Supply",
  "Refrigerated Cooling",
  "General Enquiry",
];

const INITIAL_VALUES = {
  full_name: "",
  business_name: "",
  email: "",
  phone: "",
  enquiry_type: "",
  message: "",
  consent: false,
  website: "",
};

const inputCls = (hasError) =>
  `w-full rounded-lg border bg-white px-4 py-3 text-[15px] text-ink placeholder:text-slate-400 outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/25 ${
    hasError ? "border-red-400" : "border-slate-300"
  }`;

function FieldError({ message }) {
  if (!message) return null;
  return (
    <p role="alert" className="mt-1.5 text-sm text-red-600">
      {message}
    </p>
  );
}

function validate(values) {
  const errs = {};
  if (!values.full_name.trim() || values.full_name.trim().length < 2) {
    errs.full_name = "Please enter your full name.";
  }
  if (!values.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    errs.email = "Please enter a valid email address.";
  }
  if (values.phone.replace(/\D/g, "").length < 6) {
    errs.phone = "Please enter a valid phone number.";
  }
  if (!values.enquiry_type) {
    errs.enquiry_type = "Please select an enquiry type.";
  }
  if (!values.message.trim() || values.message.trim().length < 10) {
    errs.message = "Please provide some project details (at least 10 characters).";
  }
  if (!values.consent) {
    errs.consent = "Please provide consent to be contacted about your enquiry.";
  }
  return errs;
}

export default function EnquiryForm() {
  const [values, setValues] = useState(INITIAL_VALUES);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const set = (field) => (e) => {
    const value = field === "consent" ? e.target.checked : e.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(values);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setStatus("submitting");
    setErrorMsg("");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, values);
      setStatus("success");
      toast.success("Enquiry received", {
        description: "Thank you — our team will be in touch with you shortly.",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string" && detail
          ? detail
          : "Something went wrong while sending your enquiry. Please try again, or call us directly.";
      setStatus("error");
      setErrorMsg(msg);
      toast.error("Enquiry not sent", { description: msg });
    }
  };

  const resetForm = () => {
    setValues(INITIAL_VALUES);
    setErrors({});
    setStatus("idle");
    setErrorMsg("");
  };

  if (status === "success") {
    return (
      <div className="py-10 text-center" data-testid="enquiry-success" role="status">
        <CheckCircle2 size={56} className="mx-auto text-brand" aria-hidden="true" />
        <h2 className="mt-6 font-display text-2xl font-bold text-ink">Thank you — your enquiry has been received.</h2>
        <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-slate-600">
          Our team will follow up with you as soon as practicable regarding your project requirements.
        </p>
        <button
          type="button"
          onClick={resetForm}
          data-testid="enquiry-success-reset-button"
          className="mt-8 inline-flex items-center justify-center rounded-md border border-navy/20 px-6 py-3 text-sm font-semibold text-navy transition-colors hover:border-brand hover:text-brand"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  const submitting = status === "submitting";

  return (
    <form onSubmit={handleSubmit} noValidate data-testid="enquiry-form">
      <h2 className="font-display text-2xl font-bold text-ink">Send an Enquiry</h2>
      <p className="mt-2 text-sm leading-relaxed text-slate-500">
        Fields marked with an asterisk (*) are required.
      </p>

      {status === "error" && (
        <div
          role="alert"
          data-testid="enquiry-error"
          className="mt-5 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          <AlertCircle size={18} className="mt-0.5 shrink-0 text-red-500" aria-hidden="true" />
          <span>{errorMsg}</span>
        </div>
      )}

      <div className="mt-7 grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="full_name" className="block text-sm font-medium text-ink">
            Full Name *
          </label>
          <input
            id="full_name"
            name="full_name"
            type="text"
            autoComplete="name"
            value={values.full_name}
            onChange={set("full_name")}
            aria-invalid={Boolean(errors.full_name)}
            data-testid="contact-name-input"
            className={`mt-2 ${inputCls(errors.full_name)}`}
            placeholder="Your full name"
          />
          <FieldError message={errors.full_name} />
        </div>

        <div>
          <label htmlFor="business_name" className="block text-sm font-medium text-ink">
            Business Name
          </label>
          <input
            id="business_name"
            name="business_name"
            type="text"
            autoComplete="organization"
            value={values.business_name}
            onChange={set("business_name")}
            data-testid="contact-business-input"
            className={`mt-2 ${inputCls(false)}`}
            placeholder="Optional"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-ink">
            Email Address *
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            value={values.email}
            onChange={set("email")}
            aria-invalid={Boolean(errors.email)}
            data-testid="contact-email-input"
            className={`mt-2 ${inputCls(errors.email)}`}
            placeholder="you@example.com"
          />
          <FieldError message={errors.email} />
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-ink">
            Phone Number *
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            value={values.phone}
            onChange={set("phone")}
            aria-invalid={Boolean(errors.phone)}
            data-testid="contact-phone-input"
            className={`mt-2 ${inputCls(errors.phone)}`}
            placeholder="Your contact number"
          />
          <FieldError message={errors.phone} />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="enquiry_type" className="block text-sm font-medium text-ink">
            Enquiry Type *
          </label>
          <select
            id="enquiry_type"
            name="enquiry_type"
            value={values.enquiry_type}
            onChange={set("enquiry_type")}
            aria-invalid={Boolean(errors.enquiry_type)}
            data-testid="contact-enquiry-type-select"
            className={`mt-2 ${inputCls(errors.enquiry_type)}`}
          >
            <option value="">Select an enquiry type</option>
            {ENQUIRY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <FieldError message={errors.enquiry_type} />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="message" className="block text-sm font-medium text-ink">
            Project Details / Message *
          </label>
          <textarea
            id="message"
            name="message"
            rows={5}
            value={values.message}
            onChange={set("message")}
            aria-invalid={Boolean(errors.message)}
            data-testid="contact-message-input"
            className={`mt-2 ${inputCls(errors.message)}`}
            placeholder="Tell us about your project, property type and cooling requirements"
          />
          <FieldError message={errors.message} />
        </div>

        {/* Honeypot — hidden from real users, filled by bots */}
        <div className="hidden" aria-hidden="true">
          <label htmlFor="website">Website</label>
          <input
            id="website"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={values.website}
            onChange={set("website")}
          />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="consent" className="flex items-start gap-3 text-sm leading-relaxed text-slate-700">
            <input
              id="consent"
              name="consent"
              type="checkbox"
              checked={values.consent}
              onChange={set("consent")}
              aria-invalid={Boolean(errors.consent)}
              data-testid="contact-consent-checkbox"
              className="mt-1 h-4 w-4 shrink-0 accent-[#00A8E8]"
            />
            <span>
              I consent to GSMG INVESTMENTS contacting me in relation to this enquiry. *
            </span>
          </label>
          <FieldError message={errors.consent} />
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="contact-submit-button"
        className="mt-8 inline-flex items-center justify-center gap-2.5 rounded-lg bg-brand px-8 py-3.5 font-semibold text-white shadow-lg shadow-brand/25 transition-all hover:bg-sky-500 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? (
          <>
            <Loader2 size={18} className="animate-spin" aria-hidden="true" />
            Submitting…
          </>
        ) : (
          "Submit Enquiry"
        )}
      </button>

      <p className="mt-5 text-xs leading-relaxed text-slate-500">
        Your details are used only to respond to your enquiry.
      </p>
    </form>
  );
}
